import React, { createContext, useState, useContext, useMemo, useEffect } from 'react'
import type { Theme } from '@fluentui/react-components'
import { webLightTheme, webDarkTheme } from '@fluentui/react-components'

type ThemeName = 'light' | 'dark'

type ThemeContextType = {
	theme: ThemeName
	fluentTheme: Theme
	toggleTheme: () => void
}

const STORAGE_KEY = 'theme'

const loadInitialTheme = (): ThemeName => {
	if (typeof window === 'undefined') return 'dark'
	try {
		const stored = window.localStorage.getItem(STORAGE_KEY)
		if (stored === 'light' || stored === 'dark') return stored
	} catch {
		// storage unavailable — fall through
	}
	// No stored preference: follow the OS. The site is dark-first, so only
	// an explicit OS light preference yields light mode.
	return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [theme, setTheme] = useState<ThemeName>(loadInitialTheme)

	useEffect(() => {
		try {
			window.localStorage.setItem(STORAGE_KEY, theme)
		} catch {
			// storage unavailable — non-fatal
		}
		// Expose the theme to plain CSS so page-level variables
		// (backgrounds, text colors) follow along with Fluent components.
		document.documentElement.dataset.theme = theme
	}, [theme])

	const toggleTheme = () => {
		setTheme(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'))
	}

	const fluentTheme = useMemo(
		() => (theme === 'light' ? webLightTheme : webDarkTheme),
		[theme]
	)

	const value = {
		theme,
		fluentTheme,
		toggleTheme,
	}

	return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export const useTheme = () => {
	const context = useContext(ThemeContext)
	if (context === undefined) {
		throw new Error('useTheme must be used within a ThemeProvider')
	}
	return context
}
